import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CpuChipIcon, BoltIcon, CheckBadgeIcon, XCircleIcon,
  EyeIcon, ArrowTopRightOnSquareIcon,
  FunnelIcon, TrophyIcon, BeakerIcon, LockOpenIcon,
  CalendarDaysIcon, ClockIcon
} from '@heroicons/react/24/outline';
import adminApi from '../../api/adminApi';
import Toast from '../../components/ui/Toast';

const LiveMonitor = () => {

  // ── Core State ───────────────────────────────────────────────────────────────
  const [participants, setParticipants]                   = useState([]);
  const [selectedSlot, setSelectedSlot]                   = useState('05:00 PM');
  const [competitions, setCompetitions]                   = useState([]);
  const [selectedCompetitionId, setSelectedCompetitionId] = useState(null);

  // activeTab      → top-level:  'slots' | 'participants'
  // participantTab → sub-level:  'active' | 'stage2' | 'interview' | 'winner' | 'eliminated'
  const [activeTab, setActiveTab]           = useState('slots');
  const [participantTab, setParticipantTab] = useState('active');

  const [loading, setLoading]         = useState(false);
  const [notification, setNotification] = useState(null);

  // Slot control panel
  const [slotPanelData, setSlotPanelData]       = useState([]);
  const [slotPanelLoading, setSlotPanelLoading] = useState(false);
  const [unlockingSlot, setUnlockingSlot]       = useState(null);

  const slots = ['05:00 PM', '09:00 PM', '11:00 PM'];

  // ── Load competition list for filter dropdown ────────────────────────────────
  useEffect(() => {
    const loadCompetitions = async () => {
      try {
        const data = await adminApi.getCompetitions();
        const list = data?.competitions || data || [];
        // Only type = 'competition', exclude internship / job
        setCompetitions(list.filter(c => c.type === 'competition'));
      } catch (err) {
        console.error('Failed to load competitions');
      }
    };
    loadCompetitions();
  }, []);

  // ── Slot control panel fetch ─────────────────────────────────────────────────
  const loadSlotPanel = useCallback(async () => {
    setSlotPanelLoading(true);
    try {
      const data = await adminApi.getSlotControlPanel();
      setSlotPanelData(data || []);
    } catch (err) {
      setNotification({ msg: 'Failed to load slot panel', type: 'error' });
    } finally {
      setSlotPanelLoading(false);
    }
  }, []);

  // Reload whenever the Slot Control tab is opened
  useEffect(() => {
    if (activeTab === 'slots') loadSlotPanel();
  }, [activeTab, loadSlotPanel]);

  // ── Live participants fetch ──────────────────────────────────────────────────
  const fetchLiveStatus = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      const data = await adminApi.getLiveMonitorData(selectedCompetitionId, selectedSlot);
      setParticipants(data || []);
    } catch (err) {
      setNotification({ msg: 'System Sync Failed', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedSlot, selectedCompetitionId]);

  // Poll every 30 s only while on the Participants tab
  useEffect(() => {
    if (activeTab !== 'slots') {
      fetchLiveStatus();
      const interval = setInterval(() => fetchLiveStatus(true), 30000);
      return () => clearInterval(interval);
    }
  }, [fetchLiveStatus, activeTab]);

  // ── Admin action handler ─────────────────────────────────────────────────────
  const handleAction = async (regId, nextStage, message) => {
    try {
      await adminApi.updateParticipantStatus(regId, { nextStage, noticeMessage: message });
      setNotification({ msg: `System Updated: ${nextStage}`, type: 'success' });
      fetchLiveStatus(true);
    } catch (err) {
      setNotification({ msg: 'Action Failed', type: 'error' });
    }
  };

  // ── Slot unlock handler ──────────────────────────────────────────────────────
  // Fully admin-controlled — no auto-unlock cron running
  const handleUnlockSlot = async (competitionId, slotId, slotLabel, competitionTitle) => {
    const confirmed = window.confirm(
      `CONFIRM UNLOCK\n\nCompetition: ${competitionTitle}\nSlot: ${slotLabel}\n\n` +
      `This will unlock Stage 1 for all participants in this slot.\n` +
      `This cannot be undone.\n\nProceed?`
    );
    if (!confirmed) return;

    setUnlockingSlot(slotId);
    try {
      const result = await adminApi.unlockSlot(competitionId, slotId);
      setNotification({
        msg: `✅ Stage 1 Unlocked for ${result.unlockedCount} participants in ${slotLabel}`,
        type: 'success',
      });
      loadSlotPanel(); // Refresh counts
    } catch (err) {
      setNotification({ msg: 'Unlock Failed. Check console.', type: 'error' });
    } finally {
      setUnlockingSlot(null);
    }
  };

  // ── Smart filtering — driven by participantTab ───────────────────────────────
  const filteredData = useMemo(() => {
    return participants.filter(p => {
      // Header competition dropdown filter
      const matchesComp =
        !selectedCompetitionId ||
        String(p.competition_id) === String(selectedCompetitionId);

      let matchesTab = false;

      switch (participantTab) {

        case 'active':
          // Stage 1 in progress:
          // not yet winner / eliminated / interview,
          // stage 2 still locked, stage 1 not failed
          matchesTab =
            p.status !== 'winner'     &&
            p.status !== 'eliminated' &&
            p.status !== 'interview'  &&
            p.stage_2_status === 'locked' &&
            p.stage_1_status !== 'failed';
          break;

        case 'stage2':
          // Passed Stage 1, submitted project, awaiting admin review
          matchesTab =
            p.stage_2_status === 'completed' &&
            p.status !== 'interview'  &&
            p.status !== 'winner'     &&
            p.status !== 'eliminated';
          break;

        case 'interview':
          // Admin promoted to live interview stage
          matchesTab = p.status === 'interview';
          break;

        case 'winner':
          matchesTab = p.status === 'winner';
          break;

        case 'eliminated':
          // Explicitly eliminated OR failed Stage 1
          matchesTab =
            p.status === 'eliminated' ||
            p.stage_1_status === 'failed';
          break;

        default:
          matchesTab = true;
      }

      return matchesComp && matchesTab;
    });
  }, [participants, selectedCompetitionId, participantTab]);

  // ── Stage badge colour helper ────────────────────────────────────────────────
  const getStatusStyle = (stage) => {
    switch (stage) {
      case 'STAGE_1_LIVE':    return 'border-blue-500/30 text-blue-400 bg-blue-500/5';
      case 'STAGE_2_LIVE':    return 'border-purple-500/30 text-purple-400 bg-purple-500/5';
      case 'AWAITING_REVIEW': return 'border-yellow-500/30 text-yellow-400 bg-yellow-500/5 animate-pulse';
      default:                return 'border-gray-700 text-gray-500';
    }
  };

  // ── Group slot panel rows by competition ─────────────────────────────────────
  const groupedSlotData = useMemo(() => {
    const groups = {};
    slotPanelData.forEach(row => {
      if (!groups[row.competition_id]) {
        groups[row.competition_id] = {
          competition_id:    row.competition_id,
          competition_title: row.competition_title,
          battle_date:       row.battle_date,
          end_date:          row.end_date,
          slots:             [],
        };
      }
      groups[row.competition_id].slots.push(row);
    });
    return Object.values(groups);
  }, [slotPanelData]);

  // Guard — participants must be an array before rendering cards
  if (!Array.isArray(participants)) return null;

  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen space-y-6 text-gray-100 p-6 font-sans bg-[#050505]">

      {/* TOAST */}
      {notification && (
        <Toast
          message={notification.msg}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* ── HEADER ──────────────────────────────────────────────────────────── */}
      <div className="flex flex-col lg:flex-row justify-between gap-6 bg-gray-900/40 border border-gray-800/50 p-8 rounded-[3rem] backdrop-blur-xl">
        <div className="space-y-2">
          <h1 className="text-3xl font-black tracking-tighter flex items-center gap-4">
            <div className="p-3 bg-red-600/10 rounded-2xl border border-red-600/20">
              <CpuChipIcon className="h-8 w-8 text-red-500" />
            </div>
            COMMAND CENTER
          </h1>
          <div className="flex items-center gap-4 text-xs font-mono text-gray-500">
            <span className="flex items-center gap-1.5">
              <div className="h-1.5 w-1.5 rounded-full bg-green-500 animate-ping" />
              {participants.length} CONNECTED
            </span>
            <span className="text-gray-800">|</span>
            <span>SLOT: {selectedSlot}</span>
          </div>
        </div>

        {/* Filters — only visible on Participants tab */}
        {activeTab !== 'slots' && (
          <div className="flex flex-wrap items-center gap-3">

            {/* Competition dropdown */}
            <div className="relative">
              <select
                value={selectedCompetitionId || 'ALL'}
                onChange={e =>
                  setSelectedCompetitionId(e.target.value === 'ALL' ? null : e.target.value)
                }
                className="bg-black/50 border border-gray-800 text-xs font-bold px-4 py-3 rounded-2xl appearance-none pr-10 focus:ring-2 ring-red-500/20 outline-none transition-all"
              >
                <option value="ALL">All Competitions</option>
                {competitions.map(c => (
                  <option key={c.id} value={c.id}>{c.title.toUpperCase()}</option>
                ))}
              </select>
              <FunnelIcon className="h-4 w-4 text-gray-600 absolute right-3 top-3.5 pointer-events-none" />
            </div>

            {/* Slot time filter */}
            <div className="flex bg-black/50 p-1 rounded-2xl border border-gray-800">
              {slots.map(slot => (
                <button
                  key={slot}
                  onClick={() => setSelectedSlot(slot)}
                  className={`px-5 py-2 rounded-xl text-[10px] font-black transition-all ${
                    selectedSlot === slot
                      ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                      : 'text-gray-500 hover:text-gray-300'
                  }`}
                >
                  {slot}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── TOP-LEVEL TABS: Slot Control | Participants ──────────────────────── */}
      <div className="flex gap-2 border-b border-gray-900 pb-2 overflow-x-auto">
        {[
          { id: 'slots',        label: 'Slot Control', icon: LockOpenIcon, color: 'text-green-500', desc: 'Unlock Stage 1' },
          { id: 'participants', label: 'Participants',  icon: BoltIcon,     color: 'text-blue-500',  desc: 'Live Status'   },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-t-2xl text-[11px] font-black transition-all border-b-2 whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-red-600 bg-red-600/5 text-white'
                : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
          >
            <tab.icon className={`h-4 w-4 ${activeTab === tab.id ? tab.color : ''}`} />
            {tab.label.toUpperCase()}
            <span className={`text-[9px] ${activeTab === tab.id ? 'text-gray-400' : 'text-gray-700'}`}>
              {tab.desc}
            </span>
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          SLOT CONTROL PANEL
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'slots' && (
        <div className="space-y-6">

          <div className="flex items-center gap-3 px-2">
            <LockOpenIcon className="h-5 w-5 text-green-500" />
            <div>
              <p className="text-sm font-black text-white">Slot Unlock Control</p>
              <p className="text-[10px] text-gray-500 font-mono">
                Admin manually unlocks Stage 1 per slot. Auto-unlock is disabled.
              </p>
            </div>
          </div>

          {slotPanelLoading ? (
            <div className="py-20 text-center animate-pulse text-gray-600 font-mono italic text-sm">
              Loading Slot Data...
            </div>
          ) : groupedSlotData.length === 0 ? (
            <div className="py-20 text-center border-2 border-dashed border-gray-900 rounded-[3rem]">
              <BeakerIcon className="h-12 w-12 text-gray-800 mx-auto mb-4" />
              <p className="text-gray-600 font-bold uppercase tracking-widest text-xs">
                No active competitions found
              </p>
            </div>
          ) : (
            groupedSlotData.map(comp => (
              <div
                key={comp.competition_id}
                className="bg-gray-900/30 border border-gray-800/60 rounded-[2rem] overflow-hidden"
              >
                {/* Competition header row */}
                <div className="bg-gray-900/60 px-8 py-5 border-b border-gray-800/50 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                  <div>
                    <h2 className="text-base font-black text-white tracking-tight">
                      {comp.competition_title}
                    </h2>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="flex items-center gap-1.5 text-[10px] font-mono text-gray-500">
                        <CalendarDaysIcon className="h-3 w-3" />
                        Battle Day:{' '}
                        {new Date(comp.battle_date).toLocaleDateString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })}
                      </span>
                      <span className="flex items-center gap-1.5 text-[10px] font-mono text-gray-500">
                        Apply Deadline:{' '}
                        {new Date(comp.end_date).toLocaleDateString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-blue-500/10 px-3 py-1.5 rounded-xl border border-blue-500/20">
                    <span className="text-[9px] font-black text-blue-400 uppercase tracking-widest">
                      {comp.slots.reduce((sum, s) => sum + parseInt(s.total_users || 0), 0)} Total Registered
                    </span>
                  </div>
                </div>

                {/* 3-column slot grid */}
                <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {comp.slots.map(slot => {
                    const total           = parseInt(slot.total_users    || 0);
                    const locked          = parseInt(slot.locked_count   || 0);
                    const unlocked        = parseInt(slot.unlocked_count || 0);
                    const completed       = parseInt(slot.completed_count|| 0);
                    const isFullyUnlocked = locked === 0 && total > 0;
                    const isUnlocking     = unlockingSlot === slot.slot_id;
                    const hasUsers        = total > 0;

                    return (
                      <div
                        key={slot.slot_id}
                        className={`p-5 rounded-2xl border transition-all ${
                          isFullyUnlocked
                            ? 'border-green-500/30 bg-green-500/5'
                            : 'border-gray-800 bg-black/20'
                        }`}
                      >
                        {/* Slot time + badge */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <ClockIcon className={`h-4 w-4 ${isFullyUnlocked ? 'text-green-400' : 'text-gray-500'}`} />
                            <span className="text-sm font-black text-white">{slot.slot_label}</span>
                          </div>
                          {isFullyUnlocked && (
                            <span className="text-[8px] font-black text-green-500 bg-green-500/10 px-2 py-0.5 rounded-full border border-green-500/20 uppercase">
                              Unlocked
                            </span>
                          )}
                        </div>

                        {/* Stats: registered / locked / in battle / completed */}
                        <div className="grid grid-cols-2 gap-2 mb-4">
                          <div className="bg-black/30 rounded-xl p-2.5 text-center">
                            <p className="text-lg font-black text-white">{total}</p>
                            <p className="text-[8px] text-gray-500 uppercase font-bold">Registered</p>
                          </div>
                          <div className="bg-black/30 rounded-xl p-2.5 text-center">
                            <p className="text-lg font-black text-yellow-400">{locked}</p>
                            <p className="text-[8px] text-gray-500 uppercase font-bold">Locked</p>
                          </div>
                          <div className="bg-black/30 rounded-xl p-2.5 text-center">
                            <p className="text-lg font-black text-blue-400">{unlocked}</p>
                            <p className="text-[8px] text-gray-500 uppercase font-bold">In Battle</p>
                          </div>
                          <div className="bg-black/30 rounded-xl p-2.5 text-center">
                            <p className="text-lg font-black text-green-400">{completed}</p>
                            <p className="text-[8px] text-gray-500 uppercase font-bold">Completed</p>
                          </div>
                        </div>

                        {/* Unlock button — disabled after all users unlocked */}
                        <button
                          onClick={() => handleUnlockSlot(
                            comp.competition_id,
                            slot.slot_id,
                            slot.slot_label,
                            comp.competition_title,
                          )}
                          disabled={isFullyUnlocked || !hasUsers || isUnlocking}
                          className={`w-full py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${
                            isFullyUnlocked
                              ? 'bg-green-900/20 text-green-700 cursor-not-allowed border border-green-900/30'
                              : !hasUsers
                              ? 'bg-gray-900/50 text-gray-700 cursor-not-allowed border border-gray-800'
                              : isUnlocking
                              ? 'bg-yellow-600/20 text-yellow-400 cursor-wait border border-yellow-600/30 animate-pulse'
                              : 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/20 border border-blue-500/30'
                          }`}
                        >
                          <LockOpenIcon className="h-3.5 w-3.5" />
                          {isUnlocking
                            ? 'Unlocking...'
                            : isFullyUnlocked
                            ? 'Already Unlocked'
                            : !hasUsers
                            ? 'No Participants'
                            : `Unlock Stage 1 (${locked} users)`}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          PARTICIPANTS PANEL
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'participants' && (
        <>
          {/* ── Sub-tab navigation ── */}
          <div className="flex gap-2 flex-wrap border-b border-gray-900 pb-2 overflow-x-auto">
            {[
              { id: 'active',     label: 'Stage 1 Live',   icon: BoltIcon,       color: 'text-blue-500'   },
              { id: 'stage2',     label: 'Stage 2 Review', icon: BeakerIcon,     color: 'text-purple-500' },
              { id: 'interview',  label: 'Interview',      icon: TrophyIcon,     color: 'text-yellow-500' },
              { id: 'winner',     label: 'Victory Hall',   icon: CheckBadgeIcon, color: 'text-green-500'  },
              { id: 'eliminated', label: 'Eliminated',     icon: XCircleIcon,    color: 'text-red-500'    },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setParticipantTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-t-2xl text-[10px] font-black transition-all border-b-2 whitespace-nowrap ${
                  participantTab === tab.id
                    ? 'border-red-600 bg-red-600/5 text-white'
                    : 'border-transparent text-gray-500 hover:text-gray-300'
                }`}
              >
                <tab.icon className={`h-3.5 w-3.5 ${participantTab === tab.id ? tab.color : ''}`} />
                {tab.label.toUpperCase()}
              </button>
            ))}
          </div>

          {/* ── Participant cards ── */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {loading ? (
              <div className="col-span-full py-20 text-center animate-pulse text-gray-600 font-mono italic text-sm">
                Synchronizing Arena Data...
              </div>
            ) : filteredData.length === 0 ? (
              <div className="col-span-full py-20 text-center border-2 border-dashed border-gray-900 rounded-[3rem]">
                <BeakerIcon className="h-12 w-12 text-gray-800 mx-auto mb-4" />
                <p className="text-gray-600 font-bold uppercase tracking-widest text-xs">
                  No targets found in this sector
                </p>
              </div>
            ) : (
              filteredData.map(p => (
                <div
                  key={p.registration_id}
                  className="bg-gray-900/30 border border-gray-800/60 p-6 rounded-[2rem] hover:border-gray-700 transition-all group relative overflow-hidden"
                >
                  {/* ── Card top: name / email / badges ── */}
                  <div className="flex justify-between items-start gap-4">
                    <div className="space-y-1 flex-1">

                      <h3 className="font-black text-lg tracking-tight group-hover:text-red-500 transition-colors">
                        {p.name}
                      </h3>
                      <p className="text-[10px] font-mono text-gray-500">{p.email}</p>

                      {/* Stage badge + competition title + slot label */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        <span className={`px-3 py-1 rounded-lg text-[9px] font-black border uppercase tracking-tighter ${getStatusStyle(p.current_stage)}`}>
                          {p.current_stage ? p.current_stage.replace(/_/g, ' ') : 'UNKNOWN'}
                        </span>
                        <span className="px-3 py-1 rounded-lg text-[9px] font-black border border-gray-800 text-gray-400 bg-black/20">
                          {p.title}
                        </span>
                        {/* Slot badge — shows which time slot the user selected */}
                        {p.slot_label && (
                          <span className="px-3 py-1 rounded-lg text-[9px] font-black border border-blue-900/50 text-blue-400 bg-blue-900/10">
                            🕐 {p.slot_label}
                          </span>
                        )}
                      </div>

                      {/* Stage 1 score + submission time — only on stage2 & interview tabs */}
                      {(participantTab === 'stage2' || participantTab === 'interview') && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="px-2 py-0.5 rounded text-[9px] font-black border border-green-800/50 text-green-400 bg-green-900/10">
                            S1 SCORE: {parseFloat(p.score_blitz || 0).toFixed(1)}%
                          </span>
                          {p.stage_2_submitted_at && (
                            <span className="px-2 py-0.5 rounded text-[9px] font-black border border-purple-800/50 text-purple-400 bg-purple-900/10">
                              SUBMITTED:{' '}
                              {new Date(p.stage_2_submitted_at).toLocaleString('en-IN', {
                                day: '2-digit', month: 'short',
                                hour: '2-digit', minute: '2-digit',
                              })}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ── Quick-link icon buttons — always visible top-right ── */}
                    <div className="flex flex-col gap-2 shrink-0">
                      {p.github_url && (
                        <a
                          href={p.github_url}
                          target="_blank"
                          rel="noreferrer"
                          className="p-2 bg-purple-500/10 border border-purple-500/20 rounded-xl text-purple-400 hover:bg-purple-500 hover:text-white transition-all shadow-sm"
                          title="View GitHub"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </a>
                      )}
                      {p.live_url && (
                        <a
                          href={p.live_url}
                          target="_blank"
                          rel="noreferrer"
                          className="p-2 bg-green-500/10 border border-green-500/20 rounded-xl text-green-400 hover:bg-green-500 hover:text-white transition-all shadow-sm"
                          title="View Live URL"
                        >
                          <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* ── Card footer: contact left / action buttons right ── */}
                  <div className="mt-6 pt-4 border-t border-gray-800/50 flex items-center justify-between gap-2 flex-wrap">

                    {/* Mobile number */}
                    <div className="text-[10px] font-black text-gray-600 flex items-center gap-2">
                      <BoltIcon className="h-3 w-3" />
                      {p.mobile_number || 'NO CONTACT'}
                    </div>

                    {/* ── Action buttons — change per participantTab ── */}
                    <div className="flex gap-2 flex-wrap justify-end">

                      {/* ─────────────────────────────────────────────────────
                          STAGE 1 TAB
                          Admin can only eliminate here.
                          Passing Stage 1 auto-unlocks Stage 2 via submitQuizResults.
                      ───────────────────────────────────────────────────── */}
                      {participantTab === 'active' && (
                        <button
                          onClick={() => handleAction(
                            p.registration_id,
                            'ELIMINATED',
                            'Evaluation Failed. You have been disqualified from this competition.',
                          )}
                          className="px-4 py-2 bg-red-900/20 text-red-500 rounded-xl text-[9px] font-black hover:bg-red-600 hover:text-white transition-all"
                        >
                          ELIMINATE
                        </button>
                      )}

                      {/* ─────────────────────────────────────────────────────
                          STAGE 2 TAB
                          Admin reviews submitted project.
                          APPROVE → shortlists for live interview.
                          REJECT  → eliminates from competition.
                      ───────────────────────────────────────────────────── */}
                      {participantTab === 'stage2' && (
                        <>
                          {/* GitHub link — labelled for quick review */}
                          {p.github_url && (
                            <a
                              href={p.github_url}
                              target="_blank"
                              rel="noreferrer"
                              className="px-3 py-2 bg-purple-500/10 border border-purple-500/20 rounded-xl text-[9px] font-black text-purple-400 hover:bg-purple-600 hover:text-white transition-all flex items-center gap-1"
                            >
                              <EyeIcon className="h-3 w-3" /> GITHUB
                            </a>
                          )}

                          {/* Live URL link */}
                          {p.live_url && (
                            <a
                              href={p.live_url}
                              target="_blank"
                              rel="noreferrer"
                              className="px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-xl text-[9px] font-black text-green-400 hover:bg-green-600 hover:text-white transition-all flex items-center gap-1"
                            >
                              <ArrowTopRightOnSquareIcon className="h-3 w-3" /> LIVE
                            </a>
                          )}

                          {/* Approve — moves to interview stage */}
                          <button
                            onClick={() => handleAction(
                              p.registration_id,
                              'AWAITING_VIVA',
                              'Project Approved! You have been shortlisted for a Live Interview. Our team will contact you shortly. 🚀',
                            )}
                            className="px-4 py-2 bg-blue-600 rounded-xl text-[9px] font-black hover:bg-blue-500 transition-all"
                          >
                            APPROVE → INTERVIEW
                          </button>

                          {/* Reject — eliminates */}
                          <button
                            onClick={() => handleAction(
                              p.registration_id,
                              'ELIMINATED',
                              'Project Rejected. You have been eliminated from the competition. Thank you for participating.',
                            )}
                            className="px-4 py-2 bg-red-900/20 text-red-500 rounded-xl text-[9px] font-black hover:bg-red-600 hover:text-white transition-all"
                          >
                            REJECT
                          </button>
                        </>
                      )}

                      {/* ─────────────────────────────────────────────────────
                          INTERVIEW TAB
                          Admin has conducted the external live interview.
                          GRANT WIN    → champion, winner cert auto-issued.
                          NOT SELECTED → eliminated, participation cert available.
                      ───────────────────────────────────────────────────── */}
                      {participantTab === 'interview' && (
                        <>
                          <button
                            onClick={() => handleAction(
                              p.registration_id,
                              'COMPLETED_WIN',
                              'Congratulations! You have been selected as a Champion! 🏆 Check your Achievements for your Winner Certificate.',
                            )}
                            className="px-4 py-2 bg-yellow-500 text-black rounded-xl text-[9px] font-black hover:bg-yellow-400 transition-all shadow-lg shadow-yellow-500/20 animate-pulse"
                          >
                            🏆 GRANT WIN
                          </button>
                          <button
                            onClick={() => handleAction(
                              p.registration_id,
                              'ELIMINATED',
                              'Thank you for your effort. Unfortunately you were not selected in the final round. Your Participation Certificate is available in Achievements.',
                            )}
                            className="px-4 py-2 bg-red-900/20 text-red-500 rounded-xl text-[9px] font-black hover:bg-red-600 hover:text-white transition-all"
                          >
                            NOT SELECTED
                          </button>
                        </>
                      )}

                      {/* ── WINNER TAB — read-only ── */}
                      {participantTab === 'winner' && (
                        <div className="text-[10px] font-black text-green-500 flex items-center gap-1">
                          <CheckBadgeIcon className="h-4 w-4" /> CHAMPION LOGGED
                        </div>
                      )}

                      {/* ── ELIMINATED TAB — read-only ── */}
                      {participantTab === 'eliminated' && (
                        <div className="text-[10px] font-black text-red-500 flex items-center gap-1">
                          <XCircleIcon className="h-4 w-4" /> DISQUALIFIED
                        </div>
                      )}

                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

    </div>
  );
};

export default LiveMonitor;